import {
  isConcludedSale,
  refundedAmountOf,
  SaleKind,
  SaleRecord,
  sumOf,
} from "./SaleRecord.ts";
import { isWithin, SalesBucket, SalesPeriod } from "./salesPeriod.ts";

export type SalesSummary = {
  soldInCents: number;
  netSoldInCents: number;
  orderCount: number;
  averageOrderInCents: number;
  collectedInCents: number;
  collectedFromSettlementsInCents: number;
  refundedInCents: number;
};

export function salesConcludedIn(
  records: SaleRecord[],
  period: SalesPeriod,
): SaleRecord[] {
  return records.filter(
    (record) =>
      isConcludedSale(record) && isWithin(record.orderedAtUtc, period),
  );
}

export function ordersPlacedIn(
  records: SaleRecord[],
  period: SalesPeriod,
): SaleRecord[] {
  return records.filter(
    (record) =>
      record.kind === SaleKind.Sale && isWithin(record.orderedAtUtc, period),
  );
}

export function collectedIn(
  records: SaleRecord[],
  period: SalesPeriod,
): number {
  return sumOf(records, (record) =>
    sumOf(
      record.payments.filter((payment) => isWithin(payment.paidAtUtc, period)),
      (payment) => payment.amountInCents,
    ),
  );
}

export function summarizeSales(
  records: SaleRecord[],
  period: SalesPeriod,
): SalesSummary {
  const concludedSales = salesConcludedIn(records, period);
  const soldInCents = sumOf(concludedSales, (sale) => sale.amountDueInCents);
  const refundedInCents = sumOf(concludedSales, refundedAmountOf);
  return {
    soldInCents,
    netSoldInCents: soldInCents - refundedInCents,
    orderCount: concludedSales.length,
    averageOrderInCents:
      concludedSales.length > 0
        ? Math.round(soldInCents / concludedSales.length)
        : 0,
    collectedInCents: collectedIn(records, period),
    collectedFromSettlementsInCents: collectedIn(
      records.filter((record) => record.kind === SaleKind.Settlement),
      period,
    ),
    refundedInCents,
  };
}

export function summariesByBucket(
  records: SaleRecord[],
  buckets: SalesBucket[],
): { bucket: SalesBucket; summary: SalesSummary }[] {
  return buckets.map((bucket) => ({
    bucket,
    summary: summarizeSales(records, { from: bucket.start, to: bucket.end }),
  }));
}

export function hasActivityIn(
  records: SaleRecord[],
  period: SalesPeriod,
): boolean {
  return records.some(
    (record) =>
      isWithin(record.orderedAtUtc, period) ||
      record.payments.some((payment) => isWithin(payment.paidAtUtc, period)),
  );
}

export function relativeChange(
  current: number,
  previous: number,
): number | null {
  return previous === 0 ? null : (current - previous) / previous;
}
